import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { FikoSahne } from './FikoSahne';

/**
 * 🎯 FikoSmartPoint - Akıllı Bağlantı Noktası Sistemi
 *
 * Gelişmiş özellikler:
 * - Sabit boyut (zoom-independent)
 * - Ekrana paralel (billboard)
 * - Hover tooltip/hint
 * - Animasyonlu pulse efekti
 * - Kategori bazlı renk kodlaması
 * - Çoklu ikon desteği
 */

/**
 * Smart Point kategorileri
 */
export type SmartPointCategory =
    | 'info'      // Bilgi noktası (mavi)
    | 'warning'   // Uyarı noktası (turuncu)
    | 'error'     // Hata noktası (kırmızı)
    | 'success'   // Onay noktası (yeşil)
    | 'measure'   // Ölçüm noktası (mor)
    | 'reference' // Referans noktası (cyan)
    | 'custom';   // Özel renk

/**
 * Smart Point konfigürasyonu
 */
export interface FikoSmartPointConfig {
    /** Nokta başlığı */
    baslik: string;
    /** Detaylı açıklama (tooltip'te gösterilir) */
    aciklama?: string;
    /** Kategori (renk ve ikon belirler) */
    kategori?: SmartPointCategory;
    /** Özel renk (kategori 'custom' ise) */
    renk?: string;
    /** Özel ikon (emoji veya unicode) */
    ikon?: string;
    /** Nokta boyutu (piksel) */
    boyut?: number;
    /** Sabit boyut (zoom'dan bağımsız) */
    sabitBoyut?: boolean;
    /** Pulse animasyonu aktif */
    pulseAktif?: boolean;
    /** Etiket her zaman görünsün */
    etiketGorunsun?: boolean;
    /** Özel metadata */
    metadata?: Record<string, any>;
}

/**
 * Kategori renk ve ikon tanımları
 */
const KATEGORI_STILLERI: Record<SmartPointCategory, { renk: string; ikon: string; glowRenk: string }> = {
    info: { renk: '#2196F3', ikon: 'ℹ️', glowRenk: 'rgba(33, 150, 243, 0.6)' },
    warning: { renk: '#FF9800', ikon: '⚠️', glowRenk: 'rgba(255, 152, 0, 0.6)' },
    error: { renk: '#F44336', ikon: '❌', glowRenk: 'rgba(244, 67, 54, 0.6)' },
    success: { renk: '#4CAF50', ikon: '✓', glowRenk: 'rgba(76, 175, 80, 0.6)' },
    measure: { renk: '#9C27B0', ikon: '📏', glowRenk: 'rgba(156, 39, 176, 0.6)' },
    reference: { renk: '#00BCD4', ikon: '📍', glowRenk: 'rgba(0, 188, 212, 0.6)' },
    custom: { renk: '#607D8B', ikon: '●', glowRenk: 'rgba(96, 125, 139, 0.6)' }
};

/**
 * Varsayılan konfigürasyon
 */
const DEFAULT_CONFIG: Required<FikoSmartPointConfig> = {
    baslik: 'Nokta',
    aciklama: '',
    kategori: 'info',
    renk: '#2196F3',
    ikon: 'ℹ️',
    boyut: 32,
    sabitBoyut: true,
    pulseAktif: true,
    etiketGorunsun: false,
    metadata: {}
};

/**
 * FikoSmartPoint - 3D Akıllı Nokta
 */
export class FikoSmartPoint extends THREE.Group {
    override type = 'FikoSmartPoint';

    // Konfigürasyon
    private config: Required<FikoSmartPointConfig>;

    // 3D bileşenler
    private pointMesh: THREE.Mesh | null = null;
    private glowMesh: THREE.Mesh | null = null;
    private pulseRing: THREE.Mesh | null = null;

    // CSS2D bileşenler (HTML overlay)
    private labelObject: CSS2DObject | null = null;
    private tooltipObject: CSS2DObject | null = null;
    private labelElement: HTMLDivElement | null = null;
    private tooltipElement: HTMLDivElement | null = null;

    // İnteraktif hover alanı (CSS2D)
    private hoverAreaObject: CSS2DObject | null = null;
    private hoverAreaElement: HTMLDivElement | null = null;

    // Sahne referansları
    public sahne: FikoSahne | null = null;

    // Durum
    private isSelected: boolean = false;
    private isActive: boolean = false;  // Tıklama ile aktif/pasif (label/tooltip toggle)
    private pulsePhase: number = 0;

    // Sabit boyut desteği
    private originalScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    // Animasyon
    private animationId: number | null = null;

    constructor(config?: Partial<FikoSmartPointConfig>) {
        super();

        // Konfigürasyonu birleştir
        const kategori = config?.kategori || 'info';
        const kategoriStil = KATEGORI_STILLERI[kategori];

        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
            renk: config?.renk || kategoriStil.renk,
            ikon: config?.ikon || kategoriStil.ikon
        };

        this.name = `SmartPoint_${this.config.baslik}`;
        this.renderOrder = 1000;
    }

    /**
     * Noktayı başlat ve oluştur
     */
    init(): void {
        this.createPointMesh();
        this.createGlowEffect();
        this.createPulseRing();
        this.createHoverArea();
        this.createLabel();
        this.createTooltip();

        if (this.config.pulseAktif) {
            this.startPulseAnimation();
        }
    }

    /**
     * Ana nokta mesh'i oluştur
     */
    private createPointMesh(): void {
        const size = this.config.boyut * 0.01; // Piksel -> 3D birim

        // Daire geometri
        const geometry = new THREE.CircleGeometry(size, 32);

        // Material
        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.config.renk),
            transparent: true,
            opacity: 0.95,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        this.pointMesh = new THREE.Mesh(geometry, material);
        this.pointMesh.renderOrder = 1001;
        this.pointMesh.userData.isSmartPoint = true;
        this.pointMesh.userData.parentSmartPoint = this;

        this.add(this.pointMesh);
    }

    /**
     * Glow efekti oluştur
     */
    private createGlowEffect(): void {
        const size = this.config.boyut * 0.015;
        const kategoriStil = KATEGORI_STILLERI[this.config.kategori];

        const geometry = new THREE.CircleGeometry(size, 32);

        // Gradient glow için shader material
        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.config.renk),
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        this.glowMesh = new THREE.Mesh(geometry, material);
        this.glowMesh.renderOrder = 1000;
        this.glowMesh.position.z = -0.001; // Noktanın arkasında

        this.add(this.glowMesh);
    }

    /**
     * Pulse animasyon ringi oluştur
     */
    private createPulseRing(): void {
        const innerRadius = this.config.boyut * 0.012;
        const outerRadius = this.config.boyut * 0.014;

        const geometry = new THREE.RingGeometry(innerRadius, outerRadius, 32);

        const material = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.config.renk),
            transparent: true,
            opacity: 0.6,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        this.pulseRing = new THREE.Mesh(geometry, material);
        this.pulseRing.renderOrder = 999;
        this.pulseRing.position.z = -0.002;
        this.pulseRing.visible = this.config.pulseAktif;

        this.add(this.pulseRing);
    }

    /**
     * İnteraktif alan - Sadece ikon, tıklayınca info box açılır
     */
    private createHoverArea(): void {
        this.hoverAreaElement = document.createElement('div');
        this.hoverAreaElement.className = 'fiko-smart-point-hover-area';
        this.hoverAreaElement.setAttribute('data-interactive', 'true');

        Object.assign(this.hoverAreaElement.style, {
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            cursor: 'pointer',
            pointerEvents: 'auto',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            background: `radial-gradient(circle, ${this.config.renk}dd 0%, ${this.config.renk}88 70%, transparent 100%)`,
            boxShadow: `0 0 6px ${this.config.renk}`,
            transition: 'opacity 0.15s, transform 0.15s'
        });

        this.hoverAreaElement.textContent = this.config.ikon;

        this.hoverAreaElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleActive();
        });

        this.hoverAreaObject = new CSS2DObject(this.hoverAreaElement);
        this.hoverAreaObject.position.set(0, 0, 0);
        this.add(this.hoverAreaObject);
    }

    /**
     * Info box - Tıklayınca açılan kompakt bilgi kutusu
     */
    private createLabel(): void {
        this.labelElement = document.createElement('div');
        this.labelElement.className = 'fiko-smart-point-label';
        this.labelElement.setAttribute('data-interactive', 'true');

        Object.assign(this.labelElement.style, {
            background: `linear-gradient(135deg, rgba(20,20,20,0.95) 0%, rgba(40,40,40,0.9) 100%)`,
            color: '#FFF',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            pointerEvents: 'auto',
            userSelect: 'none',
            borderLeft: `3px solid ${this.config.renk}`,
            display: 'none',
            opacity: '0',
            transition: 'opacity 0.15s',
            transform: 'translate(-50%, -50%)',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            cursor: 'pointer'
        });

        // Başlık + açıklama (varsa)
        let content = `<b style="color:${this.config.renk}">${this.config.baslik}</b>`;
        if (this.config.aciklama) {
            content += `<br><span style="color:#999;font-size:9px">${this.config.aciklama}</span>`;
        }
        this.labelElement.innerHTML = content;

        // Kapatma için tıklama
        this.labelElement.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleActive();
        });

        this.labelObject = new CSS2DObject(this.labelElement);
        this.labelObject.position.set(0, 0, 0);
        this.add(this.labelObject);

        if (this.config.etiketGorunsun) {
            this.showLabel();
            this.hoverAreaElement.style.opacity = '0';
            this.hoverAreaElement.style.pointerEvents = 'none';
        }
    }

    /**
     * Tooltip - Artık kullanılmıyor, label ile birleştirildi
     */
    private createTooltip(): void {
        // Label içinde gösteriliyor
    }

    /**
     * Pulse animasyonunu başlat
     */
    private startPulseAnimation(): void {
        if (this.animationId !== null) return;
        const animate = () => {
            this.pulsePhase += 0.03;

            if (this.pulseRing) {
                // Scale pulse
                const scale = 1 + Math.sin(this.pulsePhase) * 0.3;
                this.pulseRing.scale.set(scale, scale, 1);

                // Opacity pulse
                const opacity = 0.3 + Math.sin(this.pulsePhase) * 0.3;
                (this.pulseRing.material as THREE.MeshBasicMaterial).opacity = opacity;
            }

            // Glow pulse
            if (this.glowMesh) {
                const glowOpacity = 0.2 + Math.sin(this.pulsePhase * 0.5) * 0.1;
                (this.glowMesh.material as THREE.MeshBasicMaterial).opacity = glowOpacity;
            }

            this.animationId = requestAnimationFrame(animate);
        };

        animate();
    }

    /**
     * Label'ı göster
     */
    showLabel(): void {
        if (this.labelElement) {
            this.labelElement.style.display = 'block';
            this.labelElement.style.opacity = '1';
        }
    }

    /**
     * Label'ı gizle, ikonu göster
     */
    hideLabel(): void {
        if (this.labelElement && !this.config.etiketGorunsun) {
            this.labelElement.style.opacity = '0';
            setTimeout(() => {
                if (this.labelElement && !this.isActive) {
                    this.labelElement.style.display = 'none';
                }
            }, 150);
        }
        // İkonu geri getir
        if (this.hoverAreaElement) {
            this.hoverAreaElement.style.opacity = '1';
            this.hoverAreaElement.style.pointerEvents = 'auto';
        }
    }

    /**
     * Tooltip - Artık kullanılmıyor
     */
    showTooltip(): void {}
    hideTooltip(): void {}

    /**
     * Aktif durumu toggle et (tıklama ile)
     * İkon gizlenir, info box görünür
     */
    toggleActive(): void {
        this.isActive = !this.isActive;

        if (this.isActive) {
            // İkonu gizle
            if (this.hoverAreaElement) {
                this.hoverAreaElement.style.opacity = '0';
                this.hoverAreaElement.style.pointerEvents = 'none';
            }
            // Info box göster
            this.showLabel();
        } else {
            // Info box gizle, ikon göster
            this.hideLabel();
        }
    }

    /**
     * Aktif durumu ayarla
     */
    setActive(active: boolean): void {
        if (this.isActive !== active) {
            this.toggleActive();
        }
    }

    /**
     * Seçim durumunu ayarla
     */
    setSelected(selected: boolean): void {
        this.isSelected = selected;

        if (this.pointMesh) {
            const material = this.pointMesh.material as THREE.MeshBasicMaterial;
            if (selected) {
                material.color.set('#FFD700'); // Altın rengi
            } else {
                material.color.set(this.config.renk);
            }
        }
    }

    /**
     * Pozisyonu ayarla
     */
    setPosition(pos: THREE.Vector3): void {
        this.position.copy(pos);
    }

    /**
     * Sahneye hazırla
     */
    hazirla(sahne: FikoSahne): void {
        this.sahne = sahne;

        // Kamera değişim dinleyicisi
        if (!this.hazirlaListenerEklendi && sahne.anaGosterim?.aktifKontrol) {
            sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = true;
        }

        // İlk scale ayarı
        this.kameraYonDegisti();
    }

    /**
     * Kamera değiştiğinde sabit boyut ve z-index ayarla
     */
    private kameraYonDegisti(): void {
        if (!this.sahne?.anaGosterim?.aktifKamera) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;

        // Sabit boyut hesaplama
        if (this.config.sabitBoyut) {
            const zoom = kamera?.zoom ?? 1;
            const viewHeight = (kamera.top - kamera.bottom) / zoom;
            const targetSize = this.config.boyut / 500;
            const scale = viewHeight * targetSize;
            this.scale.set(scale, scale, scale);
        }

        // Billboard - kameraya bak
        this.quaternion.copy(kamera.quaternion);

        // Z-index hesapla (kameraya yakın = yüksek z-index = üstte görünür)
        const distance = this.position.distanceTo(kamera.position);
        const zIndex = Math.max(1, Math.floor(100000 / Math.max(1, distance)));

        if (this.hoverAreaElement) {
            this.hoverAreaElement.style.zIndex = String(zIndex);
        }
        if (this.labelElement) {
            this.labelElement.style.zIndex = String(zIndex + 1); // Label biraz üstte
        }
    }

    /**
     * Her frame'de güncelle (billboard için)
     */
    update(): void {
        if (!this.sahne?.anaGosterim?.aktifKamera) return;

        // Billboard - her zaman kameraya bak
        this.quaternion.copy(this.sahne.anaGosterim.aktifKamera.quaternion);
    }

    /**
     * Konfigürasyonu güncelle
     */
    updateConfig(config: Partial<FikoSmartPointConfig>): void {
        Object.assign(this.config, config);

        // Rengi güncelle
        if (config.renk && this.pointMesh) {
            (this.pointMesh.material as THREE.MeshBasicMaterial).color.set(config.renk);
        }

        // Label'ı güncelle
        if (this.labelElement && (config.baslik || config.ikon)) {
            this.labelElement.innerHTML = `
                <span style="margin-right: 4px;">${this.config.ikon}</span>
                <span>${this.config.baslik}</span>
            `;
        }
    }

    /**
     * Temizle
     */
    dispose(): void {
        // Animasyonu durdur
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // Event listener'ı kaldır
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
        }

        // Geometrileri temizle
        if (this.pointMesh) {
            this.pointMesh.geometry.dispose();
            (this.pointMesh.material as THREE.Material).dispose();
        }
        if (this.glowMesh) {
            this.glowMesh.geometry.dispose();
            (this.glowMesh.material as THREE.Material).dispose();
        }
        if (this.pulseRing) {
            this.pulseRing.geometry.dispose();
            (this.pulseRing.material as THREE.Material).dispose();
        }

        // CSS2D elementleri temizle
        if (this.hoverAreaElement?.parentNode) {
            this.hoverAreaElement.parentNode.removeChild(this.hoverAreaElement);
        }
        if (this.labelElement?.parentNode) {
            this.labelElement.parentNode.removeChild(this.labelElement);
        }
        if (this.tooltipElement?.parentNode) {
            this.tooltipElement.parentNode.removeChild(this.tooltipElement);
        }

        this.clear();
    }

    /**
     * JSON serialization
     */
    toJSON(): any {
        return {
            type: this.type,
            uuid: this.uuid,
            position: this.position.toArray(),
            config: this.config
        };
    }

    /**
     * JSON'dan oluştur
     */
    static fromJSON(json: any, sahne?: FikoSahne): FikoSmartPoint {
        const point = new FikoSmartPoint(json.config);
        point.uuid = json.uuid;
        point.position.fromArray(json.position);
        point.init();

        if (sahne) {
            point.hazirla(sahne);
        }

        return point;
    }
}

// Export helper
export { KATEGORI_STILLERI, DEFAULT_CONFIG as SmartPointDefaultConfig };
